/**
 * DrivingLicenseExpenses — Accounts sub-page dedicated to the
 * driving-license expenses booked in TAP. Read-only ledger with
 * on-demand invoice / receipt viewers.
 *
 * Backed by /api/accounts/driving-license-expenses (list) and
 * /api/accounts/driving-license-expenses/{id} (lazy full doc fetch).
 */
import { useCallback, useEffect, useState } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { Card, CardContent } from './ui/card';
import { Input } from './ui/input';
import { Label } from './ui/label';
import { Button } from './ui/button';
import { Badge } from './ui/badge';
import {
  Table, TableBody, TableCell, TableHead, TableHeader, TableRow,
} from './ui/table';
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from './ui/select';
import { FileText, Receipt, Loader2, RefreshCcw, Download, AlertTriangle, Filter } from 'lucide-react';
import { formatErrorMessage } from '../utils/formatError';

const API = process.env.REACT_APP_BACKEND_URL + '/api';

const PAYMENT_METHOD_LABELS = {
  bank_transfer: 'Bank Transfer',
  card_payment: 'Credit Card',
  e_wallet: 'E-Wallet',
  pms_sync: 'PMS Loan Entry',
};

const formatMoney = (n) =>
  new Intl.NumberFormat('en-AE', {
    style: 'currency', currency: 'AED', minimumFractionDigits: 2,
  }).format(Number(n || 0));

const DrivingLicenseExpenses = () => {
  const [entries, setEntries] = useState([]);
  const [totalAmount, setTotalAmount] = useState(0);
  const [totalCount, setTotalCount] = useState(0);
  const [duplicateCount, setDuplicateCount] = useState(0);
  const [showDupOnly, setShowDupOnly] = useState(false);
  const [sourceFilter, setSourceFilter] = useState('all'); // all | TAP | PMS
  const [loading, setLoading] = useState(false);
  const [fromDate, setFromDate] = useState('');
  const [toDate, setToDate] = useState('');
  const [paymentMethod, setPaymentMethod] = useState('all');
  const [q, setQ] = useState('');

  const getAuth = () => {
    const token = localStorage.getItem('access_token');
    return { headers: { Authorization: `Bearer ${token}` } };
  };

  const loadEntries = useCallback(async () => {
    setLoading(true);
    try {
      const params = { limit: 500 };
      if (fromDate) params.from_date = fromDate;
      if (toDate) params.to_date = toDate;
      if (paymentMethod && paymentMethod !== 'all') params.payment_method = paymentMethod;
      if (q) params.q = q;
      const r = await axios.get(`${API}/accounts/driving-license-expenses`, {
        ...getAuth(), params, timeout: 90_000,
      });
      setEntries(r.data.entries || []);
      setTotalAmount(r.data.total_amount || 0);
      setTotalCount(r.data.total_count || 0);
      setDuplicateCount(r.data.duplicate_count || 0);
    } catch (e) {
      toast.error(formatErrorMessage(e, 'Failed to load driving license expenses'));
    } finally {
      setLoading(false);
    }
  }, [fromDate, toDate, paymentMethod, q]);

  useEffect(() => { loadEntries(); }, [loadEntries]);

  const openDoc = async (entry, kind) => {
    // kind = 'invoice' or 'receipt'
    const urlKey = `${kind}_url`;
    const needsFetch = entry[`${kind}_needs_fetch`];
    let url = entry[urlKey];
    if (needsFetch || !url) {
      try {
        const r = await axios.get(
          `${API}/accounts/driving-license-expenses/${entry.id}`,
          { ...getAuth(), timeout: 30_000 },
        );
        url = r.data?.[urlKey] || '';
      } catch (err) {
        toast.error(formatErrorMessage(err, `Failed to load ${kind}`));
        return;
      }
    }
    if (!url) {
      toast.error(`${kind === 'invoice' ? 'Invoice' : 'Receipt'} not available`);
      return;
    }
    // Handle base64 or normal URL — open a viewer in a new tab.
    if (url.startsWith('data:')) {
      const w = window.open('', '_blank');
      if (!w) return toast.error('Popup blocked — allow popups to view');
      w.document.write(`
        <html><head><title>${kind}</title></head>
        <body style="margin:0"><iframe src="${url}" style="width:100vw;height:100vh;border:0"></iframe></body>
        </html>`);
      w.document.close();
    } else {
      window.open(url, '_blank');
    }
  };

  const clearFilters = () => {
    setFromDate(''); setToDate(''); setPaymentMethod('all'); setQ('');
    setShowDupOnly(false); setSourceFilter('all');
  };

  // Client-side filter applied AFTER the backend load — the backend has
  // already narrowed by date/payment/text, we just filter locally by
  // source-badge + duplicate toggle.
  const visibleEntries = entries.filter((e) => {
    if (showDupOnly && !e.is_duplicate) return false;
    if (sourceFilter !== 'all' && (e.source || '') !== sourceFilter) return false;
    return true;
  });

  const downloadCsv = async () => {
    try {
      const token = localStorage.getItem('access_token');
      const params = new URLSearchParams();
      if (fromDate) params.append('from_date', fromDate);
      if (toDate) params.append('to_date', toDate);
      if (paymentMethod && paymentMethod !== 'all') params.append('payment_method', paymentMethod);
      if (q) params.append('q', q);
      const r = await fetch(
        `${API}/accounts/driving-license-expenses/export.csv?${params.toString()}`,
        { headers: { Authorization: `Bearer ${token}` } },
      );
      if (!r.ok) throw new Error(`HTTP ${r.status}`);
      const blob = await r.blob();
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      // Reuse filename hint from Content-Disposition when present.
      const cd = r.headers.get('content-disposition') || '';
      const nameMatch = cd.match(/filename="?([^";]+)"?/i);
      a.download = nameMatch ? nameMatch[1] : 'driving_license_expenses.csv';
      document.body.appendChild(a);
      a.click();
      a.remove();
      window.URL.revokeObjectURL(url);
      toast.success('CSV downloaded');
    } catch (err) {
      toast.error(formatErrorMessage(err, 'Failed to download CSV'));
    }
  };

  return (
    <div className="space-y-4" data-testid="driving-license-expenses">
      <Card>
        <CardContent className="p-4 sm:p-6 space-y-4">
          {/* Filters */}
          <div className="grid grid-cols-1 md:grid-cols-5 gap-3">
            <div>
              <Label className="text-xs">From date</Label>
              <Input
                type="date" value={fromDate}
                onChange={(e) => setFromDate(e.target.value)}
                data-testid="dl-filter-from-date"
              />
            </div>
            <div>
              <Label className="text-xs">To date</Label>
              <Input
                type="date" value={toDate}
                onChange={(e) => setToDate(e.target.value)}
                data-testid="dl-filter-to-date"
              />
            </div>
            <div>
              <Label className="text-xs">Payment method</Label>
              <Select value={paymentMethod} onValueChange={setPaymentMethod}>
                <SelectTrigger data-testid="dl-filter-payment-method">
                  <SelectValue placeholder="All" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All methods</SelectItem>
                  <SelectItem value="bank_transfer">Bank Transfer</SelectItem>
                  <SelectItem value="card_payment">Credit Card</SelectItem>
                  <SelectItem value="e_wallet">E-Wallet</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label className="text-xs">Search</Label>
              <Input
                placeholder="Name, UID, ref, paid-to, school, expense type…"
                value={q}
                onChange={(e) => setQ(e.target.value)}
                data-testid="dl-filter-search"
              />
            </div>
            <div className="flex items-end gap-2">
              <Button
                variant="outline" onClick={clearFilters} size="sm"
                data-testid="dl-clear-filters"
              >
                Clear
              </Button>
              <Button
                variant="outline" onClick={loadEntries} size="sm"
                disabled={loading}
                data-testid="dl-refresh"
              >
                {loading ? <Loader2 className="w-3 h-3 animate-spin" /> : <RefreshCcw className="w-3 h-3" />}
              </Button>
            </div>
          </div>

          {/* Summary row + Export */}
          <div className="flex flex-wrap items-center justify-between gap-3 text-sm text-slate-600">
            <div className="flex flex-wrap items-center gap-3">
              <span data-testid="dl-total-count">
                <span className="font-semibold">{totalCount}</span> entries
              </span>
              <span className="text-slate-300">·</span>
              <span data-testid="dl-total-amount">
                Total: <span className="font-semibold text-slate-800">{formatMoney(totalAmount)}</span>
              </span>
              {duplicateCount > 0 && (
                <>
                  <span className="text-slate-300">·</span>
                  <button
                    type="button"
                    onClick={() => setShowDupOnly((v) => !v)}
                    className={`inline-flex items-center gap-1 px-2 py-1 rounded-md text-xs font-medium transition ${
                      showDupOnly
                        ? 'bg-amber-500 text-white'
                        : 'bg-amber-50 text-amber-800 border border-amber-200 hover:bg-amber-100'
                    }`}
                    data-testid="dl-duplicate-toggle"
                    title={showDupOnly ? 'Show all rows' : 'Show only potential duplicates'}
                  >
                    <AlertTriangle className="w-3 h-3" />
                    {duplicateCount} potential {duplicateCount === 1 ? 'duplicate' : 'duplicates'}
                    {showDupOnly && ' · showing only'}
                  </button>
                </>
              )}
              <span className="text-slate-300">·</span>
              <div className="inline-flex items-center gap-1 text-xs" data-testid="dl-source-filter">
                <Filter className="w-3 h-3 text-slate-400" />
                {['all', 'TAP', 'PMS'].map((s) => (
                  <button
                    key={s}
                    type="button"
                    onClick={() => setSourceFilter(s)}
                    className={`px-2 py-0.5 rounded-md border transition ${
                      sourceFilter === s
                        ? (s === 'TAP'
                          ? 'bg-emerald-600 text-white border-emerald-600'
                          : s === 'PMS'
                          ? 'bg-indigo-600 text-white border-indigo-600'
                          : 'bg-slate-700 text-white border-slate-700')
                        : 'bg-white text-slate-600 border-slate-200 hover:bg-slate-50'
                    }`}
                  >
                    {s === 'all' ? 'All sources' : s === 'TAP' ? 'TAP direct' : 'PMS Loan Entry'}
                  </button>
                ))}
              </div>
            </div>
            <Button
              variant="outline"
              size="sm"
              onClick={downloadCsv}
              disabled={loading || totalCount === 0}
              className="gap-1"
              data-testid="dl-download-csv"
            >
              <Download className="w-4 h-4" /> Download CSV
            </Button>
          </div>

          {/* Table */}
          <div className="border rounded-md overflow-x-auto">
            <Table>
              <TableHeader>
                <TableRow className="bg-slate-50">
                  <TableHead className="text-xs">Employee ID</TableHead>
                  <TableHead className="text-xs">Employee Name</TableHead>
                  <TableHead className="text-xs">Source</TableHead>
                  <TableHead className="text-xs">Date</TableHead>
                  <TableHead className="text-xs">Paid to</TableHead>
                  <TableHead className="text-xs text-right">Amount</TableHead>
                  <TableHead className="text-xs">Payment Type</TableHead>
                  <TableHead className="text-xs">Expense sub-type</TableHead>
                  <TableHead className="text-xs">Payment method</TableHead>
                  <TableHead className="text-xs">Invoice</TableHead>
                  <TableHead className="text-xs">Receipt</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {loading ? (
                  <TableRow>
                    <TableCell colSpan={11} className="text-center py-8 text-slate-500">
                      <Loader2 className="w-4 h-4 animate-spin inline mr-2" /> Loading…
                    </TableCell>
                  </TableRow>
                ) : visibleEntries.length === 0 ? (
                  <TableRow>
                    <TableCell colSpan={11} className="text-center py-8 text-slate-500">
                      {entries.length === 0
                        ? 'No driving license expenses found'
                        : 'No rows match the current on-screen filter'}
                    </TableCell>
                  </TableRow>
                ) : visibleEntries.map((e) => (
                  <TableRow
                    key={e.id}
                    data-testid={`dl-row-${e.id}`}
                    className={e.is_duplicate ? 'bg-amber-50/60' : ''}
                  >
                    <TableCell className="font-mono text-xs">{e.employee_id}</TableCell>
                    <TableCell className="text-xs">{e.employee_name}</TableCell>
                    <TableCell>
                      {e.source === 'PMS' ? (
                        <Badge className="bg-indigo-100 text-indigo-800 border-indigo-200 text-[10px] px-1.5 py-0.5" title="Row created via Talent PMS Loan Entry (PMS → TAP mirror)">
                          PMS
                        </Badge>
                      ) : (
                        <Badge className="bg-emerald-100 text-emerald-800 border-emerald-200 text-[10px] px-1.5 py-0.5" title="Row created directly on the TAP expense form">
                          TAP
                        </Badge>
                      )}
                    </TableCell>
                    <TableCell className="text-xs whitespace-nowrap">{e.expense_date}</TableCell>
                    <TableCell className="text-xs">{e.paid_to || '-'}</TableCell>
                    <TableCell className="text-xs text-right font-semibold">
                      <div className="inline-flex items-center gap-1 justify-end">
                        {e.is_duplicate && (
                          <AlertTriangle
                            className="w-3.5 h-3.5 text-amber-600 shrink-0"
                            title={`Possible duplicate — ${e.duplicate_count} rows share this candidate + amount + date. ${
                              e.duplicate_index === 0 ? 'This is the earliest (likely the one to keep).' : 'Newer duplicate.'
                            }`}
                            data-testid={`dl-dup-flag-${e.id}`}
                          />
                        )}
                        {formatMoney(e.amount)}
                      </div>
                    </TableCell>
                    <TableCell className="text-xs">
                      {e.payment_type ? (
                        <Badge variant="outline" className="text-xs whitespace-normal break-words max-w-[140px] inline-block leading-tight py-1">
                          {e.payment_type}
                        </Badge>
                      ) : '-'}
                    </TableCell>
                    <TableCell className="text-xs">{e.expense_subtype || '-'}</TableCell>
                    <TableCell className="text-xs">
                      {PAYMENT_METHOD_LABELS[e.payment_method] || e.payment_method || '-'}
                    </TableCell>
                    <TableCell>
                      {e.has_invoice ? (
                        <button
                          type="button"
                          onClick={() => openDoc(e, 'invoice')}
                          className="text-blue-600 text-xs hover:underline inline-flex items-center gap-1"
                          data-testid={`dl-view-invoice-${e.id}`}
                        >
                          <FileText className="w-3 h-3" /> View
                        </button>
                      ) : <span className="text-slate-400">—</span>}
                    </TableCell>
                    <TableCell>
                      {e.has_receipt ? (
                        <button
                          type="button"
                          onClick={() => openDoc(e, 'receipt')}
                          className="text-green-600 text-xs hover:underline inline-flex items-center gap-1"
                          data-testid={`dl-view-receipt-${e.id}`}
                        >
                          <Receipt className="w-3 h-3" /> View
                        </button>
                      ) : <span className="text-slate-400">—</span>}
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </div>
        </CardContent>
      </Card>
    </div>
  );
};

export default DrivingLicenseExpenses;
